import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from "@devhop/ui";
import { formatDateTime } from "@/shared/lib/date-time";
import { useEffect, useState } from "react";
import type { CreditPaymentRowDTO } from "../api/client";
import { useVoidCreditPaymentMutation } from "../api/queries";

export type VoidCreditPaymentDialogProps = {
  invoiceId: string;
  payment: CreditPaymentRowDTO;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function VoidCreditPaymentDialog({
  invoiceId,
  payment,
  open,
  onOpenChange,
  onSuccess,
}: VoidCreditPaymentDialogProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const voidMutation = useVoidCreditPaymentMutation(invoiceId);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("ກະລຸນາປ້ອນເຫດຜົນ");
      return;
    }

    setError(null);
    try {
      await voidMutation.mutateAsync({
        paymentId: payment.id,
        reason: trimmed,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      const msg =
        e instanceof Error && e.message
          ? e.message
          : "ຍົກເລີກການຮັບເງິນລົ້ມເຫຼວ";
      setError(msg);
    }
  };

  const isSubmitting = voidMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        onPointerDownOutside={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>ຍົກເລີກການຮັບເງິນ</DialogTitle>
          <p className="text-muted-foreground text-sm">
            ຍົກເລີກການບັນທຶກການຮັບເງິນທີ່ຜິດ — ຍອດຄ້າງຈະຖືກຄິດໃໝ່
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1 rounded-lg border bg-muted/30 p-4 text-sm">
            <p>
              <span className="text-muted-foreground">ວັນທີ:</span>{" "}
              {formatDateTime(payment.paidAt)}
            </p>
            <p>
              <span className="text-muted-foreground">ຈຳນວນ:</span>{" "}
              <span className="font-medium tabular-nums">
                {payment.amount.toLocaleString("lo-LA")} ກີບ
              </span>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="void-credit-payment-reason">
              ເຫດຜົນ <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="void-credit-payment-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="ປ້ອນເຫດຜົນການຍົກເລີກ..."
              rows={4}
              className="resize-none"
              disabled={isSubmitting}
            />
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            ປິດ
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "ກຳລັງຍົກເລີກ..." : "ຢືນຢັນຍົກເລີກ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
